/**
 * Course Library Slice
 * Manages state for browsing and searching courses to add to learning paths
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchCourseLibrary } from "@/api/fetchCourseLibrary";
import type { Course } from "@/modules/learningCenter/types/course";
import type { FetchCourseLibraryParams } from "../apiTypes";

/**
 * Course library state
 */
export interface CourseLibraryState {
  courses: Course[];
  total: number;
  page: number;
  pageSize: number;
  searchTerm: string;
  categoryFilter: number | null;
  minDuration: number | null;
  maxDuration: number | null;
  statusFilter: "published" | "draft" | "all";
  loading: boolean;
  error: string | null;
}

/**
 * Initial state
 */
const initialState: CourseLibraryState = {
  courses: [],
  total: 0,
  page: 1,
  pageSize: 20,
  searchTerm: "",
  categoryFilter: null,
  minDuration: null,
  maxDuration: null,
  statusFilter: "published",
  loading: false,
  error: null,
};

/**
 * Fetch courses for the library
 */
export const fetchCoursesForLibrary = createAsyncThunk(
  "courseLibrary/fetchCourses",
  async (params: FetchCourseLibraryParams | undefined, { rejectWithValue }) => {
    try {
      const response = await fetchCourseLibrary(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch course library",
      );
    }
  },
);

/**
 * Course library slice
 */
const courseLibrarySlice = createSlice({
  name: "courseLibrary",
  initialState,
  reducers: {
    /**
     * Set search term (debouncing handled in component)
     */
    setSearchTerm(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
      state.page = 1; // Reset to first page on search
    },

    /**
     * Set category filter
     */
    setCategoryFilter(state, action: PayloadAction<number | null>) {
      state.categoryFilter = action.payload;
      state.page = 1;
    },

    /**
     * Set duration range filter
     */
    setDurationFilter(
      state,
      action: PayloadAction<{ min: number | null; max: number | null }>,
    ) {
      state.minDuration = action.payload.min;
      state.maxDuration = action.payload.max;
      state.page = 1;
    },

    /**
     * Set status filter
     */
    setStatusFilter(
      state,
      action: PayloadAction<"published" | "draft" | "all">,
    ) {
      state.statusFilter = action.payload;
      state.page = 1;
    },

    /**
     * Set page number
     */
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },

    /**
     * Clear all filters
     */
    clearFilters(state) {
      state.searchTerm = "";
      state.categoryFilter = null;
      state.minDuration = null;
      state.maxDuration = null;
      state.statusFilter = "published";
      state.page = 1;
    },

    /**
     * Reset state
     */
    resetCourseLibrary() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch courses
      .addCase(fetchCoursesForLibrary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoursesForLibrary.fulfilled, (state, action) => {
        state.loading = false;
        state.courses = action.payload.data;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchCoursesForLibrary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSearchTerm,
  setCategoryFilter,
  setDurationFilter,
  setStatusFilter,
  setPage,
  clearFilters,
  resetCourseLibrary,
} = courseLibrarySlice.actions;

export default courseLibrarySlice.reducer;
